import React, { useState, useEffect, useContext } from 'react'
import { Button, Tooltip, Spinner, Checkbox, Notification, toast } from 'components/ui'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry'
import { HiOutlineCheckCircle } from 'react-icons/hi2'
import { HiOutlineTrash, HiOutlineDownload, HiOutlineX } from 'react-icons/hi'
import PostArticle from './PostArticle'
import PostListItem from './PostListItem'
import ConfirmDialog from 'components/shared/ConfirmDialog'
import { SessionContext } from 'contexts/SessionContext'
import { api as Api } from 'api/http';
import { exportPostGroupsToCsv } from './utils/exportPostGroupsCsv'
import { dispatch } from 'use-bus'
import { TbCarouselHorizontal, TbColumns1, TbColumns3, TbLayoutList, TbListCheck } from 'react-icons/tb'
import { BsStars } from 'react-icons/bs'

export const articleLayouts = [
    { id: `singlecol`, name: 'Single column', icon: <TbColumns1 />, breakPoints: { 350: 1 }, containerClassName: `max-w-md mx-auto` },
    { id: `multicols`, name: 'Multiple columns', icon: <TbColumns3 />, breakPoints: undefined, containerClassName: `` },
    { id: `list`, name: 'List view', icon: <TbLayoutList />, breakPoints: undefined, containerClassName: `` },
    // { id: `carousel`, name: 'Carousel', icon: <TbCarouselHorizontal /> },
]

const PostArticles = ({
    posts,
    hasMorePosts = false,
    getPosts = undefined,
    isLoading = false,
    breakPoints = undefined,
    expandablePost = undefined,
    forceNoEdit = undefined,
    showOptionsButton = undefined,
    showPostArticleButton = undefined,
    previewZoom = undefined,
    gap = "1.5rem",
    btnSize = undefined,
    isDraggable = undefined,
    goBack = undefined,
    showGenerateMore = undefined,
    handleOnGenerateMore = undefined,
    showFloatingBar = true,
    isSimpleView = false, // Add this line
    enableSelectionMode = false,
    forceLayout = undefined,
    syncPostsFromProp = false
}) => {

    const isPlayground = showGenerateMore;
    const { user } = useContext(SessionContext);

    const [allPosts, setAllPosts] = useState(posts);
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedKeys, setSelectedKeys] = useState(() => new Set());
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);
    const [confirmBulkDeleteOpen, setConfirmBulkDeleteOpen] = useState(false);
    const [postGroupsNeedSavingIds, setPostGroupsNeedSavingIds] = useState([]);
    const [removedPostIds, setRemovedPostIds] = useState([]);
    const [layout, setLayout] = useState(
        forceLayout
            ? forceLayout
            : isPlayground
                ? localStorage.getItem(`playgroundLayout`)
                    ? localStorage.getItem(`playgroundLayout`)
                    : `singlecol`
                : localStorage.getItem(`postsLayout`)
                    ? localStorage.getItem(`postsLayout`)
                    : `multicols`
    );

    const selectedLayout = !!layout ? articleLayouts.find(l => l.id === layout) : articleLayouts[0];
    breakPoints = breakPoints === undefined 
        ? !!selectedLayout 
            ? selectedLayout.breakPoints 
            : breakPoints 
        : breakPoints;

    const onSaveAll = () => {
        dispatch({ type: 'saveAll' });
        setPostGroupsNeedSavingIds([]);
    }

    const setPostNeedsSaving = (uniqueId) => {
        setPostGroupsNeedSavingIds((prev) => [...new Set([...(prev || []), uniqueId])]);
    }

    const onChangeLayout = (newLayout) => {
        setLayout(newLayout);
        try {
            if (isPlayground) {
                localStorage.setItem(`playgroundLayout`, newLayout);
            } else {
                localStorage.setItem(`postsLayout`, newLayout);
            }
        } catch (err) {}
    }

    const handleDuplicate = (duplicatedPostGroup) => {
        setAllPosts((prevPosts) => {
            const index = prevPosts.findIndex(post => isSamePost(post, duplicatedPostGroup));
            if (index !== -1) {
                const newPosts = [...prevPosts];
                newPosts.splice(index + 1, 0, { ...duplicatedPostGroup, id: undefined, randomId: Math.random() });
                return newPosts;
            }
            return prevPosts;
        });
    };

    const isSamePost = (post1, post2) => {
        return (
            (post1.id && post2.id && post1.id === post2.id) ||
            (post1.randomId && post2.randomId && post1.randomId === post2.randomId)
        );
    };

    const onRemovePost = (postToRemove) => {
        setAllPosts((prevPosts) => prevPosts.filter(post => !isSamePost(post, postToRemove)));
        setRemovedPostIds((prevIds) => [...prevIds, postToRemove.id || postToRemove.randomId, postToRemove.randomId || postToRemove.id]);
        setPostGroupsNeedSavingIds((prevIds) => prevIds.filter(id => id !== (postToRemove.id || postToRemove.randomId)));
    };

    const updatePostInAllPosts = (updatedPost) => {
        setAllPosts((prevPosts) => {
            const index = prevPosts.findIndex(post => isSamePost(post, updatedPost));
            if (index !== -1) {
                const newPosts = [...prevPosts];
                newPosts[index] = updatedPost;
                return newPosts;
            }
            return prevPosts;
        });
    };

    // --- Selection mode -----------------------------------------------------
    const getPostKey = (post) => post?.id || post?.randomId;

    const toggleSelectionMode = () => {
        setSelectionMode((prev) => {
            if (prev) setSelectedKeys(new Set());
            return !prev;
        });
    };

    const toggleSelect = (post) => {
        const key = getPostKey(post);
        if (key === undefined) return;
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    const selectableKeys = (allPosts || []).map(getPostKey).filter((k) => k !== undefined);
    const allSelected = selectableKeys.length > 0 && selectableKeys.every((k) => selectedKeys.has(k));

    const toggleSelectAll = () => {
        setSelectedKeys(allSelected ? new Set() : new Set(selectableKeys));
    };

    const selectedPostGroups = (allPosts || []).filter((post) => selectedKeys.has(getPostKey(post)));

    const onDownloadSelectedCsv = () => {
        if (!selectedPostGroups.length) return;
        exportPostGroupsToCsv(selectedPostGroups, user);
    };

    const onConfirmBulkDelete = async () => {
        if (isBulkDeleting || !selectedPostGroups.length) return;
        setIsBulkDeleting(true);
        const ids = selectedPostGroups.map((post) => post?.id).filter(Boolean);
        try {
            if (ids.length) {
                await Api.delete(`/postGroups/bulk`, {}, { ids });
            }
            selectedPostGroups.forEach((post) => onRemovePost(post));
            setSelectedKeys(new Set());
            setSelectionMode(false);
            toast.push(<Notification title={`${ids.length} post${ids.length === 1 ? '' : 's'} deleted`} type="success" />);
        } catch (err) {
            toast.push(<Notification title={err?.message || 'Could not delete the selected posts'} type="danger" />);
        }
        setIsBulkDeleting(false);
        setConfirmBulkDeleteOpen(false);
    };


    useEffect(() => {
        setAllPosts(prevAllPosts => {
            // Controlled mode: the parent fully owns the list (filtering,
            // pagination and refetching all happen upstream), so mirror the
            // `posts` prop directly. This drops items that are no longer in the
            // server result — required for filters to actually narrow the list —
            // while keeping client-only additions (e.g. duplicates, which have a
            // randomId but no persisted id) and honoring optimistic deletes.
            if (syncPostsFromProp) {
                const localOnly = (prevAllPosts || []).filter(p => !p?.id && p?.randomId);
                const fromServer = (posts || []).filter(post => {
                    const id = post?.id ?? post?.randomId;
                    return id === undefined || !removedPostIds?.includes(id);
                });
                return [...fromServer, ...localOnly];
            }
            const newPosts = posts?.filter(post =>
                !prevAllPosts?.some(p => isSamePost(p, post)) &&
                !removedPostIds?.includes(post?.id || post?.randomId)
            );
            const updatedPosts = prevAllPosts?.filter(post => {
                const updatedPost = posts?.find(p => isSamePost(p, post));
                return (updatedPost || !posts?.some(p => isSamePost(p, post))) &&
                       !removedPostIds?.includes(post?.id || post?.randomId);
            }).map(post => {
                const updatedPost = posts?.find(p => isSamePost(p, post));
                return updatedPost || post;
            });
            return [...(updatedPosts || []), ...(newPosts || [])];
        });
    }, [posts, removedPostIds, syncPostsFromProp]);

    return (
        <div>
            <div className={`mb-2 relative ${selectedLayout?.containerClassName}`}>
                <motion.div
                    className={`pb-6 !overflow-visible`}
                    initial={{ opacity: 0, height: 0, overflow: 'hidden' }}
                    transition={{ duration: 0.15 }}
                    animate={{
                        opacity: 1,
                        height: 'auto',
                    }}
                >
                    {layout === 'list' ? (
                        <div className='flex flex-col gap-3'>
                            {allPosts?.map((item, key) => (
                                <PostListItem
                                    key={item?.randomId || item?.id || key}
                                    item={item}
                                    selectionMode={selectionMode}
                                    selected={selectedKeys.has(getPostKey(item))}
                                    onToggleSelect={toggleSelect}
                                    onRemovePost={onRemovePost}
                                    updatePost={updatePostInAllPosts}
                                />
                            ))}
                        </div>
                    ) : (
                        <ResponsiveMasonry columnsCountBreakPoints={breakPoints}>
                            <Masonry className='' gutter={gap}>
                                {allPosts?.map((item, key) => {
                                    const article = (
                                        <PostArticle
                                            key={item?.randomId || item?.id || key}
                                            uniqueId={item?.randomId || item?.id || key}
                                            item={item}
                                            expandablePost={expandablePost}
                                            forceNoEdit={forceNoEdit}
                                            showOptionsButton={showOptionsButton}
                                            showPostArticleButton={showPostArticleButton}
                                            previewZoom={previewZoom}
                                            btnSize={btnSize}
                                            isDraggable={isDraggable}
                                            style={{ zIndex: allPosts?.length - key }}
                                            setPostNeedsSaving={setPostNeedsSaving}
                                            duplicate={handleDuplicate}
                                            onRemovePost={onRemovePost}
                                            updatePost={updatePostInAllPosts}
                                            isSimpleView={isSimpleView}
                                        />
                                    );
                                    if (!selectionMode) return article;
                                    const selected = selectedKeys.has(getPostKey(item));
                                    return (
                                        <div
                                            key={item?.randomId || item?.id || key}
                                            className={`relative isolate rounded-2xl transition-all ${selected ? 'ring-2 ring-primary-500' : ''}`}
                                            onClick={() => toggleSelect(item)}
                                        >
                                            <div className='pointer-events-none'>
                                                {article}
                                            </div>
                                            <div className='absolute inset-0 z-[300] cursor-pointer rounded-2xl' />
                                            <div className='absolute top-3 left-3 z-[310] bg-white dark:bg-gray-800 rounded-md shadow p-1 pointer-events-none'>
                                                <Checkbox checked={selected} readOnly />
                                            </div>
                                        </div>
                                    );
                                })}
                            </Masonry>
                        </ResponsiveMasonry>
                    )}
                </motion.div>
            </div>

            {hasMorePosts && <div className='flex w-full justify-center pb-8'><Button loading={isLoading} disabled={isLoading} variant="" onClick={() => getPosts && getPosts()}>Load More</Button></div>}
            
            {!!posts?.length && !!showFloatingBar &&
                <div className='fixed lg:sticky !overflow-visible bottom-0 lg:py-4 w-full lg:max-w-[650px] left-0 translate-z-[0] z-[200] mx-auto'>
                    <div className="shadow-[0_0_20px_#0000001a] !z-[10] shrink-0 flex flex-col lg:flex-row gap-2 lg:gap-4 items-center justify-between bg-secondary-500 dark:bg-gray-800 shrink-0 rounded-t-3xl lg:rounded-3xl px-2 py-2">
                        {selectionMode ? (
                            <>
                                <div className='flex items-center gap-2 lg:flex-1 w-full lg:w-auto justify-center lg:justify-start'>
                                    <Button variant="plain" size="sm" onClick={toggleSelectAll}>
                                        {allSelected ? 'Deselect all' : 'Select all'}
                                    </Button>
                                    <span className='text-sm font-semibold px-2 whitespace-nowrap'>
                                        {selectedKeys.size} selected
                                    </span>
                                </div>
                                <div className='flex items-center gap-2 w-full lg:w-auto'>
                                    <Button
                                        variant="twoTone"
                                        size="sm"
                                        className="flex-1 lg:flex-auto"
                                        icon={<HiOutlineDownload />}
                                        disabled={!selectedKeys.size}
                                        onClick={onDownloadSelectedCsv}
                                    >
                                        Download CSV
                                    </Button>
                                    <Button
                                        variant="twoTone"
                                        color="red-600"
                                        size="sm"
                                        className="flex-1 lg:flex-auto"
                                        icon={<HiOutlineTrash />}
                                        disabled={!selectedKeys.size || isBulkDeleting}
                                        loading={isBulkDeleting}
                                        onClick={() => setConfirmBulkDeleteOpen(true)}
                                    >
                                        Delete
                                    </Button>
                                    <Button
                                        variant="plain"
                                        size="sm"
                                        icon={<HiOutlineX />}
                                        onClick={toggleSelectionMode}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className='hidden lg:flex flex-1 items-center gap-2'>
                                    <div className='flex'>
                                        {articleLayouts.map((l,i) => (
                                            <Tooltip title={l.name} key={l.id}>
                                                <Button
                                                    variant="twoTone"
                                                    className={`${i == 0 ? `!rounded-r-none` : i == articleLayouts?.length-1 ? `!rounded-l-none` : `!rounded-none`}`}
                                                    icon={l.icon}
                                                    active={l.id == layout}
                                                    onClick={() => onChangeLayout(l.id)}
                                                />
                                            </Tooltip>
                                        ))}
                                    </div>
                                    {enableSelectionMode && (
                                        <Tooltip title="Select posts">
                                            <Button
                                                variant="twoTone"
                                                icon={<TbListCheck />}
                                                onClick={toggleSelectionMode}
                                            >
                                                Select
                                            </Button>
                                        </Tooltip>
                                    )}
                                </div>
                                {!!showGenerateMore &&
                                    <div className='flex-1 lg:flex-auto w-full flex items-center justify-center'>
                                        <Button
                                            variant="ai"
                                            className="flex-1 lg:flex-auto"
                                            onClick={() => handleOnGenerateMore()}
                                            icon={<BsStars />}
                                            disabled={isLoading}
                                            loading={isLoading}
                                        >
                                            Generate more
                                        </Button>
                                    </div>
                                }
                                <div className='flex flex-1 items-center gap-2 w-full lg:w-auto'>
                                    {enableSelectionMode && (
                                        <Button
                                            variant="twoTone"
                                            className="lg:hidden flex-1"
                                            icon={<TbListCheck />}
                                            onClick={toggleSelectionMode}
                                        >
                                            Select
                                        </Button>
                                    )}
                                    {!!goBack &&
                                        <Button
                                            variant="plain"
                                            className="lg:flex-auto flex-1"
                                            onClick={() => goBack()}
                                        >
                                            Go back
                                        </Button>
                                    }
                                    <Button
                                        variant="solid"
                                        className="lg:flex-auto flex-1"
                                        onClick={onSaveAll}
                                        icon={<HiOutlineCheckCircle />}
                                        disabled={!postGroupsNeedSavingIds?.length}
                                    >
                                        Save all {!!postGroupsNeedSavingIds?.length && <>({postGroupsNeedSavingIds?.length})</>}
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            }

            <ConfirmDialog
                isOpen={confirmBulkDeleteOpen}
                onClose={() => setConfirmBulkDeleteOpen(false)}
                type="danger"
                title="Delete selected posts"
                cancelText="Cancel"
                confirmText={isBulkDeleting ? 'Deleting...' : `Delete ${selectedKeys.size}`}
                confirmButtonColor="red-600"
                onCancel={() => setConfirmBulkDeleteOpen(false)}
                onConfirm={onConfirmBulkDelete}
            >
                <p>
                    Are you sure you want to delete {selectedKeys.size} selected post{selectedKeys.size === 1 ? '' : 's'}? This action cannot be undone.
                </p>
            </ConfirmDialog>
        </div>
    )
}

export default PostArticles
